/**
 * Trovva — Broker Escalation Queue
 *
 * Shows questions agents asked that the AI couldn't answer.
 * Broker responds, optionally saves answer to knowledge base
 * so the AI handles it next time.
 *
 * Used as both a dashboard widget (compact) and full view.
 */
import { useState, useEffect } from 'react';
import { MessageCircleQuestion, Send, BookPlus, Clock, CheckCircle, AlertTriangle, ChevronRight } from 'lucide-react';
import { theme } from '../../lib/theme';

export default function EscalationQueue({ user, compact = false }) {
  const [escalations, setEscalations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [response, setResponse] = useState('');
  const [saveToKB, setSaveToKB] = useState(true);
  const [sending, setSending] = useState(false);

  const token = localStorage?.getItem?.('sb-token') || '';
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = async () => {
    try {
      const [escResp, statsResp] = await Promise.all([
        fetch('/api/escalations?status=open', { headers }).then(r => r.json()),
        fetch('/api/escalations?action=stats', { headers }).then(r => r.json()),
      ]);
      setEscalations(escResp.escalations || []);
      setStats(statsResp);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleRespond = async (escId) => {
    if (!response.trim()) return;
    setSending(true);
    try {
      await fetch(`/api/escalations?id=${escId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({
          broker_response: response,
          status: 'resolved',
          save_to_kb: saveToKB,
        }),
      });
      setRespondingTo(null);
      setResponse('');
      setSaveToKB(true);
      fetchData();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const handleDismiss = async (escId) => {
    await fetch(`/api/escalations?id=${escId}`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ status: 'dismissed' }),
    });
    fetchData();
  };

  if (loading) return <div style={{ padding: 16, color: theme.colors.textSecondary, fontSize: 13 }}>Loading...</div>;

  // ── Compact widget for dashboard ──────────────────────
  if (compact) {
    const openCount = stats?.open || 0;
    return (
      <div style={{
        padding: 16, borderRadius: 12,
        border: `1px solid ${openCount > 0 ? '#f59e0b40' : theme.colors.border}`,
        background: openCount > 0 ? '#fef3c710' : 'white',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageCircleQuestion size={16} color={openCount > 0 ? '#f59e0b' : theme.colors.textSecondary} />
            <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>Agent Questions</span>
          </div>
          {openCount > 0 && (
            <span style={{
              padding: '2px 8px', borderRadius: 10,
              background: '#f59e0b', color: 'white',
              fontSize: 11, fontWeight: 700,
            }}>
              {openCount} open
            </span>
          )}
        </div>

        {openCount === 0 ? (
          <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: 0 }}>
            No pending questions — your AI is handling everything.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {escalations.slice(0, 3).map(esc => (
              <div key={esc.id} style={{
                padding: '8px 10px', borderRadius: 8,
                background: 'white', border: `1px solid ${theme.colors.border}`,
                fontSize: 13, color: theme.colors.text,
              }}>
                <div style={{ fontWeight: 500, marginBottom: 2 }}>{esc.agent_name || 'Agent'}</div>
                <div style={{ color: theme.colors.textSecondary, fontSize: 12 }}>
                  {esc.question.length > 80 ? esc.question.slice(0, 80) + '...' : esc.question}
                </div>
              </div>
            ))}
            {openCount > 3 && (
              <div style={{ fontSize: 12, color: theme.colors.primary, fontWeight: 500, textAlign: 'center' }}>
                + {openCount - 3} more
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Full view ─────────────────────────────────────────
  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text, margin: '0 0 4px' }}>
          <MessageCircleQuestion size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Agent Escalations
        </h2>
        <p style={{ fontSize: 13, color: theme.colors.textSecondary, margin: 0 }}>
          Questions your agents asked that the AI couldn't answer. Respond here — and optionally
          teach the AI so it handles the same question next time.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{
          display: 'flex', gap: 20, marginBottom: 20, padding: '12px 16px',
          background: `${theme.colors.primary}06`, borderRadius: 10,
          border: `1px solid ${theme.colors.primary}12`,
        }}>
          <Stat label="Open" value={stats.open} color="#f59e0b" />
          <Stat label="This week" value={stats.this_week} color={theme.colors.primary} />
          <Stat label="Total resolved" value={stats.resolved} color="#22c55e" />
        </div>
      )}

      {/* Escalation list */}
      {escalations.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '48px 24px',
          border: `1.5px dashed ${theme.colors.border}`, borderRadius: 12,
        }}>
          <CheckCircle size={32} color="#22c55e" style={{ opacity: 0.5, marginBottom: 12 }} />
          <p style={{ fontSize: 14, color: theme.colors.textSecondary, margin: 0 }}>
            All clear — your AI is handling all agent questions.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {escalations.map(esc => (
            <div key={esc.id} style={{
              padding: 16, borderRadius: 12,
              border: `1px solid ${theme.colors.border}`,
              background: 'white',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: theme.colors.text }}>
                    {esc.agent_name || 'Agent'}
                  </span>
                  <span style={{
                    padding: '1px 6px', borderRadius: 4,
                    background: esc.tier === 3 ? '#fef2f2' : '#fef3c7',
                    color: esc.tier === 3 ? '#dc2626' : '#92400e',
                    fontSize: 11, fontWeight: 600,
                  }}>
                    {esc.tier === 3 ? 'Needs Attorney' : 'Needs Broker'}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                  <Clock size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {timeAgo(esc.created_at)}
                </span>
              </div>

              {/* Question */}
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: `${theme.colors.primary}06`,
                fontSize: 14, color: theme.colors.text, lineHeight: 1.5,
                marginBottom: 8,
              }}>
                {esc.question}
              </div>

              {/* What AI tried */}
              {esc.ai_attempted && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 4 }}>
                    AI attempted response:
                  </div>
                  <div style={{
                    padding: '8px 10px', borderRadius: 6,
                    background: '#f3f4f6', fontSize: 13, color: '#6b7280',
                    lineHeight: 1.5, fontStyle: 'italic',
                  }}>
                    {esc.ai_attempted}
                  </div>
                </div>
              )}

              {/* Response form */}
              {respondingTo === esc.id ? (
                <div style={{ marginTop: 8 }}>
                  <textarea
                    value={response}
                    onChange={e => setResponse(e.target.value)}
                    placeholder="Type your response to the agent..."
                    rows={3}
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: 8,
                      border: `1px solid ${theme.colors.border}`, fontSize: 13,
                      fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5,
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.textSecondary, cursor: 'pointer' }}>
                      <input type="checkbox" checked={saveToKB} onChange={e => setSaveToKB(e.target.checked)} />
                      <BookPlus size={12} /> Save to Knowledge Base (AI learns this)
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setRespondingTo(null); setResponse(''); }}
                        style={{
                          padding: '6px 12px', borderRadius: 6, fontSize: 12,
                          background: 'white', color: theme.colors.textSecondary,
                          border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleRespond(esc.id)}
                        disabled={sending || !response.trim()}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: theme.colors.primary, color: 'white',
                          border: 'none', cursor: 'pointer',
                          opacity: sending ? 0.6 : 1,
                        }}
                      >
                        <Send size={12} /> {sending ? 'Sending...' : 'Respond'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button
                    onClick={() => setRespondingTo(esc.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      background: theme.colors.primary, color: 'white',
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    <Send size={12} /> Respond
                  </button>
                  <button
                    onClick={() => handleDismiss(esc.id)}
                    style={{
                      padding: '6px 12px', borderRadius: 6, fontSize: 12,
                      background: 'white', color: theme.colors.textSecondary,
                      border: `1px solid ${theme.colors.border}`, cursor: 'pointer',
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value || 0}</div>
      <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>{label}</div>
    </div>
  );
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
